/**
 * React Query hooks for the Relay Leads module endpoints.
 */
import { useAuth } from "@clerk/clerk-react";
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ApiError,
  ContactResponse,
  CreateLeadRequest,
  CreateLeadResponse,
  InboxLeadListResponse,
  LeadAnalyticsResponse,
  LeadDetailResponse,
  LeadListResponse,
  LeadMessageListResponse,
  LeadMessageResponse,
  LeadResponse,
  LeadSequenceListResponse,
  LeadSequenceResponse,
  SequenceStepListResponse,
  StartSequenceRequest,
  UpdateContactRequest,
  UpdateLeadRequest,
  UpdateLeadStatusRequest,
  UpdateSequenceStatusRequest,
} from "@/lib/api-client";
import {
  addLeadMessage,
  createLead,
  fetchLead,
  fetchLeadMessages,
  fetchLeadSequences,
  fetchLeads,
  fetchLeadsAnalytics,
  fetchLeadsInbox,
  fetchSequenceSteps,
  startLeadSequence,
  updateContact,
  updateLead,
  updateLeadStatus,
  updateSequenceStatus,
} from "@/lib/api-client";

export const leadsKeys = {
  all: ["leads"] as const,
  analytics: () => ["leads", "analytics"] as const,
  inbox: (params: { limit?: number; cursor?: string } = {}) => ["leads", "inbox", params] as const,
  list: (params: { status?: LeadResponse["status"]; limit?: number; cursor?: string } = {}) =>
    ["leads", "list", params] as const,
  detail: (id: string) => ["leads", "detail", id] as const,
  messages: (leadId: string) => ["leads", "messages", leadId] as const,
  sequences: (leadId: string) => ["leads", "sequences", leadId] as const,
  steps: (leadId: string, seqId: string) => ["leads", "sequences", leadId, "steps", seqId] as const,
} as const;

// ─── Analytics ────────────────────────────────────────────────────────────────

export type UseLeadsAnalyticsResult = UseQueryResult<LeadAnalyticsResponse, ApiError | Error>;

export function useLeadsAnalytics(): UseLeadsAnalyticsResult {
  const { getToken } = useAuth();
  return useQuery<LeadAnalyticsResponse, ApiError | Error>({
    queryKey: leadsKeys.analytics(),
    queryFn: () => fetchLeadsAnalytics({ getToken: () => getToken() }),
    staleTime: 30_000,
  });
}

// ─── List ─────────────────────────────────────────────────────────────────────

export type UseLeadsListResult = UseQueryResult<LeadListResponse, ApiError | Error>;

export function useLeadsList(
  params: { status?: LeadResponse["status"]; limit?: number; cursor?: string } = {},
): UseLeadsListResult {
  const { getToken } = useAuth();
  return useQuery<LeadListResponse, ApiError | Error>({
    queryKey: leadsKeys.list(params),
    queryFn: () => fetchLeads(params, { getToken: () => getToken() }),
  });
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export type UseLeadDetailResult = UseQueryResult<LeadDetailResponse, ApiError | Error>;

export function useLeadDetail(id: string | undefined): UseLeadDetailResult {
  const { getToken } = useAuth();
  return useQuery<LeadDetailResponse, ApiError | Error>({
    queryKey: id ? leadsKeys.detail(id) : (["leads", "detail", null] as const),
    queryFn: () => {
      if (!id) throw new Error("id is required");
      return fetchLead(id, { getToken: () => getToken() });
    },
    enabled: Boolean(id),
  });
}

// ─── Messages ─────────────────────────────────────────────────────────────────

export type UseLeadMessagesResult = UseQueryResult<LeadMessageListResponse, ApiError | Error>;

export function useLeadMessages(leadId: string | undefined): UseLeadMessagesResult {
  const { getToken } = useAuth();
  return useQuery<LeadMessageListResponse, ApiError | Error>({
    queryKey: leadsKeys.messages(leadId ?? ""),
    queryFn: () => {
      if (!leadId) throw new Error("leadId is required");
      return fetchLeadMessages(leadId, { getToken: () => getToken() });
    },
    enabled: Boolean(leadId),
    refetchInterval: 10_000,
  });
}

// ─── Create ───────────────────────────────────────────────────────────────────

export type UseCreateLeadResult = UseMutationResult<CreateLeadResponse, ApiError | Error, CreateLeadRequest>;

export function useCreateLead(): UseCreateLeadResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<CreateLeadResponse, ApiError | Error, CreateLeadRequest>({
    mutationFn: (body) => createLead(body, { getToken: () => getToken() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadsKeys.all });
    },
  });
}

// ─── Update ───────────────────────────────────────────────────────────────────

export type UseUpdateLeadResult = UseMutationResult<
  LeadResponse,
  ApiError | Error,
  { id: string; body: UpdateLeadRequest }
>;

export function useUpdateLead(): UseUpdateLeadResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<LeadResponse, ApiError | Error, { id: string; body: UpdateLeadRequest }>({
    mutationFn: ({ id, body }) => updateLead(id, body, { getToken: () => getToken() }),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: leadsKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: leadsKeys.all });
    },
  });
}

// ─── Update Status ────────────────────────────────────────────────────────────

export type UseUpdateLeadStatusResult = UseMutationResult<
  LeadResponse,
  ApiError | Error,
  { id: string; body: UpdateLeadStatusRequest }
>;

export function useUpdateLeadStatus(): UseUpdateLeadStatusResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<LeadResponse, ApiError | Error, { id: string; body: UpdateLeadStatusRequest }>({
    mutationFn: ({ id, body }) => updateLeadStatus(id, body, { getToken: () => getToken() }),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: leadsKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: leadsKeys.all });
    },
  });
}

// ─── Add Message ──────────────────────────────────────────────────────────────

export type UseAddMessageResult = UseMutationResult<
  LeadMessageResponse,
  ApiError | Error,
  { leadId: string; direction: "inbound" | "outbound"; channel: "sms" | "email" | "internal_note"; body: string }
>;

export function useAddMessage(): UseAddMessageResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    LeadMessageResponse,
    ApiError | Error,
    { leadId: string; direction: "inbound" | "outbound"; channel: "sms" | "email" | "internal_note"; body: string }
  >({
    mutationFn: ({ leadId, ...rest }) =>
      addLeadMessage(leadId, rest, { getToken: () => getToken() }),
    onSuccess: (_, { leadId }) => {
      void qc.invalidateQueries({ queryKey: leadsKeys.messages(leadId) });
      void qc.invalidateQueries({ queryKey: leadsKeys.detail(leadId) });
    },
  });
}

// ─── Inbox ────────────────────────────────────────────────────────────────────

export type UseLeadsInboxResult = UseQueryResult<InboxLeadListResponse, ApiError | Error>;

export function useLeadsInbox(
  params: { limit?: number; cursor?: string } = {},
): UseLeadsInboxResult {
  const { getToken } = useAuth();
  return useQuery<InboxLeadListResponse, ApiError | Error>({
    queryKey: leadsKeys.inbox(params),
    queryFn: () => fetchLeadsInbox(params, { getToken: () => getToken() }),
    // Polling stays for the live-tail feel on page 1; subsequent cursored
    // pages are static so polling there would just churn the cache.
    ...(params.cursor === undefined && { refetchInterval: 15_000 }),
  });
}

// ─── Sequences ────────────────────────────────────────────────────────────────

export type UseLeadSequencesResult = UseQueryResult<LeadSequenceListResponse, ApiError | Error>;

export function useLeadSequences(leadId: string | undefined): UseLeadSequencesResult {
  const { getToken } = useAuth();
  return useQuery<LeadSequenceListResponse, ApiError | Error>({
    queryKey: leadId ? leadsKeys.sequences(leadId) : (["leads", "sequences", null] as const),
    queryFn: () => {
      if (!leadId) throw new Error("leadId is required");
      return fetchLeadSequences(leadId, { getToken: () => getToken() });
    },
    enabled: Boolean(leadId),
  });
}

export type UseStartSequenceResult = UseMutationResult<
  LeadSequenceResponse,
  ApiError | Error,
  { leadId: string; body: StartSequenceRequest }
>;

export function useStartSequence(): UseStartSequenceResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<LeadSequenceResponse, ApiError | Error, { leadId: string; body: StartSequenceRequest }>({
    mutationFn: ({ leadId, body }) => startLeadSequence(leadId, body, { getToken: () => getToken() }),
    onSuccess: (_, { leadId }) => {
      void qc.invalidateQueries({ queryKey: leadsKeys.sequences(leadId) });
    },
  });
}

export type UseUpdateSequenceStatusResult = UseMutationResult<
  LeadSequenceResponse,
  ApiError | Error,
  { leadId: string; sequenceId: string; body: UpdateSequenceStatusRequest }
>;

export function useUpdateSequenceStatus(): UseUpdateSequenceStatusResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    LeadSequenceResponse,
    ApiError | Error,
    { leadId: string; sequenceId: string; body: UpdateSequenceStatusRequest }
  >({
    mutationFn: ({ leadId, sequenceId, body }) =>
      updateSequenceStatus(leadId, sequenceId, body, { getToken: () => getToken() }),
    onSuccess: (_, { leadId }) => {
      void qc.invalidateQueries({ queryKey: leadsKeys.sequences(leadId) });
    },
  });
}

// ─── Update Contact (L4) ──────────────────────────────────────────────────────

export type UseUpdateContactResult = UseMutationResult<
  ContactResponse,
  ApiError | Error,
  { contactId: string; body: UpdateContactRequest }
>;

export function useUpdateContact(leadId?: string): UseUpdateContactResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<ContactResponse, ApiError | Error, { contactId: string; body: UpdateContactRequest }>({
    mutationFn: ({ contactId, body }) => updateContact(contactId, body, { getToken: () => getToken() }),
    onSuccess: () => {
      // Invalidate the lead detail so the contact panel refreshes.
      if (leadId) void qc.invalidateQueries({ queryKey: leadsKeys.detail(leadId) });
      void qc.invalidateQueries({ queryKey: leadsKeys.all });
    },
  });
}

export type UseSequenceStepsResult = UseQueryResult<SequenceStepListResponse, ApiError | Error>;

export function useSequenceSteps(
  leadId: string | undefined,
  sequenceId: string | undefined,
): UseSequenceStepsResult {
  const { getToken } = useAuth();
  return useQuery<SequenceStepListResponse, ApiError | Error>({
    queryKey:
      leadId && sequenceId
        ? leadsKeys.steps(leadId, sequenceId)
        : (["leads", "steps", null] as const),
    queryFn: () => {
      if (!leadId || !sequenceId) throw new Error("leadId and sequenceId are required");
      return fetchSequenceSteps(leadId, sequenceId, { getToken: () => getToken() });
    },
    enabled: Boolean(leadId && sequenceId),
  });
}
